import { useDispatch, useSelector } from "react-redux";
import LinearInfo from "../../../Configurators/Linear/components/LinearInfo";
import { useState } from "react";
import { type ClusterGroup, type ClusterItem } from "../../types";
import { type RootState } from "../../../redux/store";
import { useOnMount } from "../../../common/hooks/useOnMount";
import { getTileDisplayData } from "../../api/getTileDisplayData";
import { type LoadableEntry } from "../../../redux/loadableStore/types";
import Spinner from "../../../common/components/Spinner/Spinner";
import SimpleExternalContent from "../../../Configurators/Linear/components/SimpleExternalContent";
import { updateAssistantValues } from "../../../Search/redux/actions";
import { Card } from "../../../Configurators/Linear/components/Card";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";

const useCharacteristicData = () => {
  const [data, setData] = useState<LoadableEntry<ClusterGroup[]>>({ state: "loading" });
  useOnMount(() => {
    getTileDisplayData()
      .then((data) => setData({ state: "loaded", data }))
      .catch(() => setData({ state: "error" }));
  });

  return data;
};

export const CharacteristicsFilter = () => {
  const data = useCharacteristicData();
  const { t } = useTranslation();

  if (data.state === "loading") {
    return <Spinner />;
  }

  if (data.state === "error") {
    return <SimpleExternalContent.DefaultErrorMsg />;
  }

  const characteristics = data.data!;

  return (
    <div className={"scha__sa__characteristic-list"}>
      {characteristics.map((characteristic) => (
        <div key={characteristic.code} className={"scha__sa__characteristic"}>
          <div className="scha__sa__characteristic__title">
            <span>{t(characteristic.name)}</span>
            <LinearInfo
              className={"icon-margin-l"}
              info={characteristic.description}
              popupClassName={"scha__lc__external-content--shaft"}
            />
          </div>
          <div className={"scha__sa__characteristic__tile-container"}>
            {characteristic.clusterItems.map((tile) => (
              <CharacteristicTile key={tile.code} item={tile} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const useSelectedState = (code: string) => {
  const dispatch = useDispatch();
  const activeTileCodes = useSelector((state: RootState) => state.srp.assistantValues);

  const toggle = () => dispatch(updateAssistantValues(code));
  const isSelected = activeTileCodes.includes(code);

  return [isSelected, toggle] as const;
};

const selectIsEnabled = (code: string) => (state: RootState) => {
  return !!state.srp.facetValues[code]?.values.find((facetValue) => facetValue.code === "true" && facetValue.count > 0);
};

const CharacteristicTile = ({ item }: { item: ClusterItem }) => {
  const [selected, toggleSelected] = useSelectedState(item.code);
  const enabled = useSelector(selectIsEnabled(item.code));

  const { t } = useTranslation();

  return (
    <Card
      className={"scha__sa__tile"}
      innerClassName={"scha__sa__tile__inner"}
      data-test-id={`SATile-${item.code}`}
      active={selected}
      onClick={toggleSelected}
      disabled={!enabled && !selected}
    >
      <img src={selected ? item.activeImageUrl : item.imageUrl} alt="" className="scha__sa__tile__svg" />
      <p className="scha__sa__tile__text">{t(item.text)}</p>
      <CheckedCircle className="scha__sa__tile__check" checked={selected} />
    </Card>
  );
};
